import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dateLocale } from '../../i18n/locale';
import { type PublicReview } from '../../services/reviewApi';

function formatDate(iso: string | null, language: string): string {
  if (!iso) return '';
  const locale = dateLocale(language);
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * A single published review (read-only). Long text is clamped to 3 lines with a
 * toggle. Shared by the activity detail page and the host page.
 */
export default function ReviewItem({
  review,
  language,
}: {
  review: PublicReview;
  language: string;
}) {
  const { t } = useTranslation('review');
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [review.content]);

  return (
    <li className="pt-4 first:pt-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-gray-900 text-sm">{review.displayName}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
          {t('attendedBadge')}
        </span>
        <span className="text-xs text-gray-400">{formatDate(review.publishedAt, language)}</span>
      </div>
      <p
        ref={textRef}
        className={`text-sm text-gray-700 mt-2 whitespace-pre-wrap ${expanded ? '' : 'line-clamp-3'}`}
      >
        {review.content}
      </p>
      {(overflowing || expanded) && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs text-primary hover:underline font-medium"
        >
          {expanded ? t('collapse') : t('expand')}
        </button>
      )}
      {review.adminReply && (
        <div className="mt-3 ml-4 pl-3 border-l-2 border-gray-200">
          <span className="text-xs font-semibold text-primary">{t('officialReply')}</span>
          <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{review.adminReply}</p>
        </div>
      )}
    </li>
  );
}
